import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'
import { formatCurrency } from '@hospora-bookings/shared-ui'
import { applyBrand, applyDocumentMeta, applyOrgIdentity, rememberIdentity, validBrand } from '../lib/brand'

import { apiBaseUrl } from '../lib/runtime'
const API_URL = apiBaseUrl()
const LOAD_TIMEOUT_MS = 8_000

/** Bound both the response and its body so a stalled request cannot leave Book Now disabled. */
async function fetchJson(path: string): Promise<unknown> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), LOAD_TIMEOUT_MS)
  try {
    const res = await fetch(`${API_URL}${path}`, { signal: controller.signal })
    if (!res.ok) throw new Error(`${path} ${res.status}`)
    return await res.json()
  } finally {
    clearTimeout(timeout)
  }
}

/** What GET /config says about one venue. */
export interface ConfigVenue {
  id: string
  name: string
  address: string | null
  phone: string | null
  email: string | null
  description: string | null
  imageUrl: string | null
  timezone: string | null
  hasPoolTable: boolean
  /** The venue's pool table id, when it has one. */
  poolTableId: string | null
  /** From GET /venues only (the fallback when /config is unavailable). */
  maxPartySize?: number
}

export interface FunctionArea {
  id: string
  name: string
  label: string
  description: string
  fee: number
  /** An organisation's own areas (set in the staff app) carry their terms; JB's built-in schedule has none of these. */
  capacity?: number | null
  /** Minimum spend for the whole function. */
  minSpend?: number
  /** The part of the fee paid online to secure the booking; the whole fee when absent. */
  deposit?: number | null
  includedHours?: number
  overtimeRate?: number
}

export interface OrgConfig {
  org: {
    name: string
    slug: string
    logoUrl: string | null
    brandColor: string
    country: string
    locale: string
    timezone: string
    currency: string
    contactEmail: string | null
    contactPhone: string | null
    website: string | null
    address: string | null
  } | null
  venues: ConfigVenue[]
  settings: {
    tagline?: string
    footerTagline?: string
    heroSubheadline: string
    heroImages: string[]
    contactUrl: string | null
    functionTermsUrl: string | null
    functionTermsBuiltIn: boolean
    builtInVenueRules: boolean
    /** Deposit on large-group and function bookings, as a percentage of the minimum spend. */
    depositPercent: number
    /** Hours of notice customers are asked to give when cancelling; null says nothing. */
    cancellationNoticeHours: number | null
  }
  features: { largeGroupDeposit: boolean; largeBookings: boolean; functionEvents: boolean; poolTables: boolean }
  functionAreas: Record<string, FunctionArea[]>
  functionHire?: { includedHours: number; overtimeRate: number } | null
}

/** Neutral defaults: used until /config answers, and when it cannot (an API that is momentarily the old one). */
const NEUTRAL: OrgConfig = {
  org: null,
  venues: [],
  settings: {
    heroSubheadline: 'Reserve a table online.',
    heroImages: [],
    contactUrl: null,
    functionTermsUrl: null,
    functionTermsBuiltIn: false,
    builtInVenueRules: false,
    depositPercent: 30,
    cancellationNoticeHours: null,
  },
  features: { largeGroupDeposit: false, largeBookings: false, functionEvents: false, poolTables: false },
  functionAreas: {},
}

/** "Harbour Hotel" -> "HH", "JB & Sons" -> "JS". */
export function initialsOf(name: string, max = 2): string {
  const words = name.split(/[^\p{L}\p{N}]+/u).filter(Boolean)
  const letters = words.map((w) => w[0]!.toUpperCase()).join('')
  return (letters || name.slice(0, max).toUpperCase()).slice(0, max)
}

export const useOrgStore = defineStore('org', () => {
  const config = ref<OrgConfig>(NEUTRAL)
  const status = ref<'idle' | 'loading' | 'ready' | 'failed'>('idle')
  /** True when /config answered. False when only the venue list could be read (or nothing). */
  const configured = ref(false)
  let inflight: Promise<void> | null = null

  // Pictures that came from the hub (venue photos, hero pictures, the logo) and failed to load. Whatever
  // showed one hides it instead of leaving a broken-image icon: a venue card becomes the text-only card, the
  // hero pictures disappear, the logo gives way to the organisation's name. Forgotten on a forced reload,
  // so a Retry gives the pictures another chance.
  const failedImages = reactive(new Set<string>())
  function imageFailed(url: string | null | undefined): boolean {
    return !!url && failedImages.has(url)
  }
  function markImageFailed(url: string | null | undefined): void {
    if (url) failedImages.add(url)
  }

  async function fetchConfig(): Promise<OrgConfig> {
    const body = (await fetchJson('/config')) as Partial<OrgConfig>
    if (!body || typeof body !== 'object' || !Array.isArray(body.venues)) throw new Error('config shape')
    const settings = { ...NEUTRAL.settings, ...(body.settings ?? {}) }
    try {
      if (!settings.functionTermsUrl || !/^https:\/\//i.test(settings.functionTermsUrl) || new URL(settings.functionTermsUrl).protocol !== 'https:') settings.functionTermsUrl = null
    } catch {
      settings.functionTermsUrl = null
    }
    return {
      org: body.org ?? null,
      venues: body.venues.map((v) => ({ ...v, poolTableId: v.poolTableId ?? null })),
      settings,
      features: { ...NEUTRAL.features, ...(body.features ?? {}) },
      functionAreas: body.functionAreas ?? {},
      functionHire: body.functionHire ?? null,
    }
  }

  /** The old API has no /config: its venue list is enough to keep booking, with neutral copy. */
  async function fetchVenuesOnly(): Promise<OrgConfig> {
    const list = (await fetchJson('/venues')) as Array<Record<string, any>>
    return {
      ...NEUTRAL,
      venues: list.map((v) => ({
        id: v.id,
        name: v.name,
        address: v.address ?? null,
        phone: v.phone ?? null,
        email: null,
        description: null,
        imageUrl: v.imageUrl ?? null,
        timezone: null,
        hasPoolTable: !!v.hasPoolTable,
        poolTableId: v.poolTableId ?? null,
        maxPartySize: v.maxPartySize,
      })),
      features: { ...NEUTRAL.features, poolTables: list.some((v) => v.hasPoolTable) },
    }
  }

  function apply(next: OrgConfig, wasConfigured: boolean) {
    config.value = next
    configured.value = wasConfigured
    status.value = 'ready'
    applyBrand(validBrand(next.org?.brandColor))
    // data-org + (for an organisation with bundled typefaces, i.e. JB & Sons) its fonts; neutral stacks otherwise.
    void applyOrgIdentity(next.org?.slug)
    if (next.org) rememberIdentity(next.org.slug, next.org.brandColor)
    applyDocumentMeta(next.org?.name, next.venues.map((v) => v.name))
  }

  /** Read the organisation once (shared by every caller). `force` reads again, e.g. from a Retry button. */
  function load(force = false): Promise<void> {
    if (inflight) return inflight
    if (status.value === 'ready' && configured.value && !force) return Promise.resolve()
    if (force) failedImages.clear()
    status.value = 'loading'
    inflight = (async () => {
      try {
        apply(await fetchConfig(), true)
      } catch {
        try {
          apply(await fetchVenuesOnly(), false)
        } catch {
          config.value = NEUTRAL
          configured.value = false
          status.value = 'failed'
        }
      } finally {
        inflight = null
      }
    })()
    return inflight
  }

  const org = computed(() => config.value.org)
  const orgName = computed(() => org.value?.name ?? '')
  const monogram = computed(() => initialsOf(orgName.value || 'B'))
  const locale = computed(() => org.value?.locale || 'en')
  const timezone = computed(() => org.value?.timezone || undefined)
  const currency = computed(() => org.value?.currency || '')
  const logoUrl = computed(() => org.value?.logoUrl ?? null)
  /** Payment configuration must be supplied by the organisation. */
  const region = computed(() => ({ currency: currency.value, country: org.value?.country || '' }))
  function money(value: number | null | undefined, exact = false): string {
    return formatCurrency(value, currency.value, locale.value, exact)
  }
  const venues = computed(() => config.value.venues)
  /** The only venue, when there is exactly one: the location picker is skipped for it. */
  const soleVenue = computed(() => (venues.value.length === 1 ? venues.value[0]! : null))
  /** Where "choose a location" goes: straight home when there is nothing to choose. */
  const venuesPath = computed(() => (soleVenue.value ? '/' : '/venues'))
  const settings = computed(() => config.value.settings)
  const hasFunctionTerms = computed(() => !!settings.value.functionTermsUrl || settings.value.functionTermsBuiltIn)
  const features = computed(() => config.value.features)
  const builtInVenueRules = computed(() => config.value.settings.builtInVenueRules)
  const depositPercent = computed(() => config.value.settings.depositPercent)
  const cancellationNoticeHours = computed(() => config.value.settings.cancellationNoticeHours)
  const heroImages = computed(() => config.value.settings.heroImages)
  const contactUrl = computed(() => config.value.settings.contactUrl)
  const contactEmail = computed(() => org.value?.contactEmail ?? null)
  const contactPhone = computed(() => org.value?.contactPhone ?? null)

  function venueById(id: string | null | undefined): ConfigVenue | undefined {
    return id ? venues.value.find((v) => v.id === id) : undefined
  }
  /** The venue's name, or '' (never the raw id) when it is not one of this organisation's venues. */
  function venueName(id: string | null | undefined): string {
    return venueById(id)?.name ?? ''
  }
  /** The venue as a sentence reads it: "JB & Sons Narrabeen", "Harbour Hotel" (no repetition when the name has the organisation's). */
  function venueTitle(id: string | null | undefined): string {
    const name = venueName(id).trim() || 'the venue'
    const orgLabel = orgName.value.trim()
    if (!orgLabel || name.toLowerCase().includes(orgLabel.toLowerCase())) return name
    return `${orgLabel} ${name}`
  }
  function functionAreasFor(id: string | null | undefined): FunctionArea[] {
    return id ? config.value.functionAreas[id] ?? [] : []
  }

  return {
    config, status, configured, load,
    org, orgName, monogram, locale, timezone, currency, region, logoUrl, money,
    venues, soleVenue, venuesPath,
    settings, hasFunctionTerms, features, builtInVenueRules, depositPercent, cancellationNoticeHours, heroImages, contactUrl, contactEmail, contactPhone,
    venueById, venueName, venueTitle, functionAreasFor,
    imageFailed, markImageFailed,
  }
})
